/**
 * Centralized parameter definitions for SigTDD.
 *
 * Defaults are loaded from the API (backed by PostgreSQL) on app startup.
 * The static DEFAULTS constant serves as a fallback if the API is unreachable.
 * Consumer code uses DEFAULTS directly for synchronous access — loadDefaults()
 * overwrites it in-place when the API responds.
 */

/** Defines a single numeric parameter with constraints */
export interface ParamDef {
  readonly defaultValue: number;
  readonly min: number;
  readonly max: number;
  readonly type: 'integer' | 'float';
  readonly unit: string;
  readonly description: string;
}

/** Intersection-level parameters */
export interface IntersectionParamDefs {
  readonly distanceToNext: ParamDef;
  readonly speedNorth: ParamDef;
  readonly speedSouth: ParamDef;
  readonly offset: ParamDef;
  readonly phaseDuration: ParamDef;
  readonly amber: ParamDef;
}

/** UI behavior parameters */
export interface UIParamDefs {
  readonly offsetScrollSmall: ParamDef;
  readonly offsetScrollLarge: ParamDef;
}

/** Plan-level parameters */
export interface PlanParamDefs {
  readonly cycleLength: ParamDef;
  readonly xRangeFactor: ParamDef;
}

// ============================================================================
// Display parameter interfaces — rendering options for diagram elements
// ============================================================================

/** Phase bar display parameters */
export interface PhaseBarParamDefs {
  readonly heightRatio: ParamDef;
  readonly labelFontSize: ParamDef;
  readonly labelColor: ParamDef;
  readonly borderAlpha: ParamDef;
}

/** Phase extent line parameters (dashed green/red projections) */
export interface PhaseExtentParamDefs {
  readonly startColor: ParamDef;
  readonly startAlpha: ParamDef;
  readonly endColor: ParamDef;
  readonly endAlpha: ParamDef;
  readonly dashSubdivisions: ParamDef;
  readonly dashDrawUnits: ParamDef;
}

/** Sequence band parameters (through all intersections) */
export interface SequenceParamDefs {
  readonly nbColor: ParamDef;
  readonly sbColor: ParamDef;
  readonly fillAlpha: ParamDef;
  readonly borderAlpha: ParamDef;
  readonly scanStep: ParamDef;
}

/** Next Green band parameters (hits green at next intersection) */
export interface NextGreenParamDefs {
  readonly nbColor: ParamDef;
  readonly sbColor: ParamDef;
  readonly fillAlpha: ParamDef;
  readonly borderAlpha: ParamDef;
}

/** Travel Path / Immediate Next parameters (design speed, no coordination) */
export interface TravelPathParamDefs {
  readonly nbColor: ParamDef;
  readonly sbColor: ParamDef;
  readonly fillAlpha: ParamDef;
  readonly borderAlpha: ParamDef;
}

/** Top-level config grouping all parameter definitions */
export interface DefaultsConfig {
  readonly intersection: IntersectionParamDefs;
  readonly ui: UIParamDefs;
  readonly plan: PlanParamDefs;
  readonly phaseBar: PhaseBarParamDefs;
  readonly phaseExtent: PhaseExtentParamDefs;
  readonly sequence: SequenceParamDefs;
  readonly nextGreen: NextGreenParamDefs;
  readonly travelPath: TravelPathParamDefs;
}

export const DEFAULTS: DefaultsConfig = {
  intersection: {
    distanceToNext: {
      defaultValue: 400,
      min: 50,
      max: 5000,
      type: 'integer',
      unit: 'm',
      description: 'Distance to next intersection north',
    },
    speedNorth: {
      defaultValue: 60,
      min: 1,
      max: 110,
      type: 'integer',
      unit: 'km/h',
      description: 'Travel speed north',
    },
    speedSouth: {
      defaultValue: 60,
      min: 1,
      max: 110,
      type: 'integer',
      unit: 'km/h',
      description: 'Travel speed south',
    },
    offset: {
      defaultValue: 0,
      min: 0,
      max: 999,
      type: 'integer',
      unit: 's',
      description: 'Signal offset from reference',
    },
    phaseDuration: {
      defaultValue: 36,
      min: 1,
      max: 300,
      type: 'integer',
      unit: 's',
      description: 'Default phase duration',
    },
    amber: {
      defaultValue: 4,
      min: 2,
      max: 8,
      type: 'integer',
      unit: 's',
      description: 'Amber (yellow) transition time',
    },
  },
  ui: {
    offsetScrollSmall: {
      defaultValue: 1,
      min: 1,
      max: 5,
      type: 'integer',
      unit: '',
      description: 'Offset scroll bar arrow click change',
    },
    offsetScrollLarge: {
      defaultValue: 5,
      min: 2,
      max: 10,
      type: 'integer',
      unit: '',
      description: 'Offset scroll bar inside click change',
    },
  },
  plan: {
    cycleLength: {
      defaultValue: 100,
      min: 10,
      max: 600,
      type: 'integer',
      unit: 's',
      description: 'Default cycle length when no phases are defined',
    },
    xRangeFactor: {
      defaultValue: 2.5,
      min: 1,
      max: 5,
      type: 'float',
      unit: '',
      description: 'X axis range as multiple of cycle length',
    },
  },

  // Display parameters — rendering options for diagram elements
  phaseBar: {
    heightRatio: {
      defaultValue: 40, min: 10, max: 100, type: 'integer', unit: '',
      description: 'World height divisor for bar thickness',
    },
    labelFontSize: {
      defaultValue: 14, min: 8, max: 24, type: 'integer', unit: 'px',
      description: 'Phase letter font size',
    },
    labelColor: {
      defaultValue: 0x0044ff, min: 0, max: 0xffffff, type: 'integer', unit: 'color',
      description: 'Phase letter text color',
    },
    borderAlpha: {
      defaultValue: 0.3, min: 0, max: 1, type: 'float', unit: '',
      description: 'Phase bar border opacity',
    },
  },
  phaseExtent: {
    startColor: {
      defaultValue: 0x00cc00, min: 0, max: 0xffffff, type: 'integer', unit: 'color',
      description: 'Phase start line color',
    },
    startAlpha: {
      defaultValue: 0.5, min: 0, max: 1, type: 'float', unit: '',
      description: 'Phase start line opacity',
    },
    endColor: {
      defaultValue: 0xff0000, min: 0, max: 0xffffff, type: 'integer', unit: 'color',
      description: 'Phase end line color',
    },
    endAlpha: {
      defaultValue: 0.5, min: 0, max: 1, type: 'float', unit: '',
      description: 'Phase end line opacity',
    },
    dashSubdivisions: {
      defaultValue: 40, min: 10, max: 100, type: 'integer', unit: '',
      description: 'Dashed line subdivisions',
    },
    dashDrawUnits: {
      defaultValue: 3, min: 1, max: 10, type: 'integer', unit: '',
      description: 'Dash units drawn per cycle (gap = 1)',
    },
  },
  sequence: {
    nbColor: {
      defaultValue: 0x2244cc, min: 0, max: 0xffffff, type: 'integer', unit: 'color',
      description: 'NB sequence band color',
    },
    sbColor: {
      defaultValue: 0x44aacc, min: 0, max: 0xffffff, type: 'integer', unit: 'color',
      description: 'SB sequence band color',
    },
    fillAlpha: {
      defaultValue: 0.35, min: 0, max: 1, type: 'float', unit: '',
      description: 'Sequence band fill opacity',
    },
    borderAlpha: {
      defaultValue: 0.7, min: 0, max: 1, type: 'float', unit: '',
      description: 'Sequence band boundary line opacity',
    },
    scanStep: {
      defaultValue: 0.5, min: 0.1, max: 2, type: 'float', unit: 's',
      description: 'Green window scan resolution',
    },
  },
  nextGreen: {
    nbColor: {
      defaultValue: 0x00cc00, min: 0, max: 0xffffff, type: 'integer', unit: 'color',
      description: 'NB next-green band color',
    },
    sbColor: {
      defaultValue: 0x00cc00, min: 0, max: 0xffffff, type: 'integer', unit: 'color',
      description: 'SB next-green band color',
    },
    fillAlpha: {
      defaultValue: 0.3, min: 0, max: 1, type: 'float', unit: '',
      description: 'Next-green band fill opacity',
    },
    borderAlpha: {
      defaultValue: 0.6, min: 0, max: 1, type: 'float', unit: '',
      description: 'Next-green band boundary line opacity',
    },
  },
  travelPath: {
    nbColor: {
      defaultValue: 0xaaaaaa, min: 0, max: 0xffffff, type: 'integer', unit: 'color',
      description: 'NB travel path color',
    },
    sbColor: {
      defaultValue: 0xaaaaaa, min: 0, max: 0xffffff, type: 'integer', unit: 'color',
      description: 'SB travel path color',
    },
    fillAlpha: {
      defaultValue: 0.25, min: 0, max: 1, type: 'float', unit: '',
      description: 'Travel path band fill opacity',
    },
    borderAlpha: {
      defaultValue: 0.7, min: 0, max: 1, type: 'float', unit: '',
      description: 'Travel path band boundary line opacity',
    },
  },
};

/** Clamp a value to the parameter's [min, max] range, rounding if integer */
export function clampToParam(value: number, param: ParamDef): number {
  const clamped = Math.max(param.min, Math.min(param.max, value));
  return param.type === 'integer' ? Math.round(clamped) : clamped;
}

/** Validate a value against a ParamDef. Returns null if valid, or an error string. */
export function validateParam(value: number, param: ParamDef): string | null {
  if (isNaN(value)) return 'Value must be a number';
  if (param.type === 'integer' && !Number.isInteger(value)) {
    return 'Value must be a whole number';
  }
  if (value < param.min) return `Minimum is ${param.min}${param.unit ? ' ' + param.unit : ''}`;
  if (value > param.max) return `Maximum is ${param.max}${param.unit ? ' ' + param.unit : ''}`;
  return null;
}

// ============================================================================
// API loading
// ============================================================================

/**
 * Fetch defaults from the API and overwrite the DEFAULTS object in-place.
 * All existing references to DEFAULTS stay valid because we mutate the object
 * rather than replacing it.
 *
 * Returns true if API defaults were loaded, false if using static fallback.
 */
export async function loadDefaults(): Promise<boolean> {
  try {
    const res = await fetch('/api/defaults');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data: DefaultsConfig = await res.json();

    // Overwrite DEFAULTS in-place so all existing references pick up new values
    for (const key of Object.keys(data) as (keyof DefaultsConfig)[]) {
      if (DEFAULTS[key] && data[key]) {
        Object.assign(DEFAULTS[key], data[key]);
      }
    }

    console.log('[defaults] Loaded from API');
    return true;
  } catch (err) {
    console.warn('[defaults] API unavailable, using static fallback:', err);
    return false;
  }
}
